import i18n from '@/shared/i18n/config';
import { presentServerError } from '@/shared/utils/serverErrorPresentation';
import { OperationJournalError } from '@/shared/api/utils/operationJournal';
/* ============================================================================
 * useNodeOperations.ts - Node CRUD operations and state management
 * ============================================================================ */

import type { OperationPersistenceOptions } from '../types';
import { ConfirmedWriteError } from '../types';
import { useCallback, useEffect, useRef, type MutableRefObject, type Dispatch, type SetStateAction } from 'react';
import type { KanbanColumnConfig, ProjectData, ProjectNode, NodeOperation, ViewFilter, FileAttachment } from '@/shared/types/projectData';
import { applyOperation } from '@/shared/utils/projectDataUtils';
import { undoStack } from '@/shared/commands/UndoStack';
import type { ApplicationSnapshot, ViewStates, GlobalUIState } from '@/shared/types/applicationState';
import type { SnapshotHistory } from '@/shared/utils/snapshotHistory';
import { webSocketSingleton } from '@/shared/api/WebSocketSingleton';
import { getNodeId, getProjectIdFromNodeId } from '@/features/projects/MultiProjectContext';
import { synchronizeCreateHistoryIdentity } from './utils/createOperationHistoryIdentity';
import { log } from '@/shared/utils/logger';
import { validateNodeOperation } from '@/shared/utils/nodeOperationValidation';
import { generateOperationDescription } from '@/shared/utils/operations/operationDescriptions';
import { enrichOperationWithBeforeState, ensureOperationId } from '@/shared/utils/operations/operationStateCapture';
import { transformNodeFromBackend, transformProjectDataFromBackend } from '@/shared/utils/fieldNameTransform';
import { sanitizeProjectData } from '@/shared/utils/projectDataUtils';
import {
  createCopyOperation,
  createCutOperation,
  createPasteOperation,
  createDuplicateNodeOperation,
  createDuplicateSubtreeOperation
} from '@/shared/utils/operations/clipboardOperations';
import {
  createAttachFileOperation,
  createDetachFileOperation,
  createDeleteFileOperation,
  createRenameFileOperation
} from '@/shared/utils/operations/fileOperations';
import { API_BASE_URL } from '@/shared/apiClient';
import { authService } from '@/features/auth/services/authService';
import type { User } from '@/features/auth/services/authService';
import { normalizeKanbanColumn, normalizeKanbanColumnId } from '@/shared/utils/kanbanState';
import { extractProjectIdFromOperation } from './utils/projectIdExtractor';
import { buildServerOperation, ensureProjectId } from './utils/serverOperationBuilder';
import { resolveRealtimeDispatchMode } from './utils/realtimeDispatchMode';
import { isLatestNodeOperationAck, markLatestNodeOperation } from './utils/operationAckFreshness';
import type { WebSocketManager } from '@/shared/api/WebSocketManager';
import { isAckOnlySameSessionOperationType } from './handlers/operationDeduplication';
import { normalizeOperationIds } from './handlers/nodeNormalization';
import { persistNoteDocumentUpdate } from '@/features/tasks/services/noteDocumentService';
import { persistReportDocumentBody } from '@/features/reports/services/reportDocumentService';
import { isReportBody, readReportBody } from '@/features/reports/utils/reportBody';
import { getNodeCustomField } from '@/features/editor/documentCustomFields';
import { getAuthRuntimeSessionIdentity } from '@/features/auth/services/authRuntime';
import { getAdminViewAsUserId } from '@/features/auth/services/adminViewAs';

/** User object shape needed by this hook */
type NodeOpsUser = User;

/** Snapshot history manager interface */
interface UseNodeOperationsParams {
  projectData: ProjectData | null;
  expandedNodeIds: Set<string>;
  selectedNodeIds: Set<string>;
  filters: ViewFilter;
  searchQuery: string;
  sortBy: string | null;
  isCompactView: boolean;
  user: NodeOpsUser | null;
  viewStates: ViewStates | null;
  uiState: GlobalUIState | null;
  sessionId: MutableRefObject<string>;
  snapshotHistory: MutableRefObject<SnapshotHistory>;
  ws: MutableRefObject<WebSocket | null>;
  wsManager?: MutableRefObject<WebSocketManager | null>;
  setProjectData: Dispatch<SetStateAction<ProjectData | null>>;
  beginUnconfirmedWrite?: (key?: string) => () => void;
  setError: (error: string | null) => void;
  updateUndoStateFromSnapshots: () => void;
  updateProjectInCache?: (projectId: string, updatedData: Partial<ProjectData>) => void;
}

function extractAuthoritativeNodesFromOperationResult(
  resultRecord: Record<string, unknown> | undefined
): ProjectNode[] {
  const updatedNodesFull = Array.isArray(resultRecord?.updatedNodesFull)
    ? resultRecord.updatedNodesFull
    : [];

  if (updatedNodesFull.length > 0) {
    return updatedNodesFull
      .map((node) => transformNodeFromBackend(node))
      .filter(
        (node): node is ProjectNode =>
          Boolean(node) &&
          typeof node === 'object' &&
          typeof (node as ProjectNode).id === 'string'
      );
  }

  const authoritativeNode = transformNodeFromBackend(resultRecord?.node);
  if (
    authoritativeNode &&
    typeof authoritativeNode === 'object' &&
    typeof (authoritativeNode as ProjectNode).id === 'string'
  ) {
    return [authoritativeNode as ProjectNode];
  }

  return [];
}

function mergeAuthoritativeNodesIntoProjectData(
  currentData: ProjectData,
  authoritativeNodes: ProjectNode[],
  fallbackProjectId?: string
): { data: ProjectData; changed: boolean } {
  if (authoritativeNodes.length === 0) {
    return { data: currentData, changed: false };
  }

  const authoritativeNodesById = new Map(
    authoritativeNodes.map((node) => [getNodeId(node.id), node])
  );

  let changed = false;
  const mergedNodes = currentData.nodes.map((existingNode) => {
    const authoritativeNode = authoritativeNodesById.get(getNodeId(existingNode.id));
    if (!authoritativeNode) {
      return existingNode;
    }
    const targetProjectId = authoritativeNode.projectId || fallbackProjectId;
    const existingProjectId = existingNode.projectId || getProjectIdFromNodeId(existingNode.id) || currentData.projectId;
    if (targetProjectId && existingProjectId !== targetProjectId) return existingNode;
    if (typeof authoritativeNode.version === 'number' && typeof existingNode.version === 'number'
      && authoritativeNode.version < existingNode.version) return existingNode;

    const mergedNode: ProjectNode = {
      ...existingNode,
      ...authoritativeNode,
      id: existingNode.id,
      parentId: existingNode.parentId,
      dependencies: Array.isArray(existingNode.dependencies)
        ? [...existingNode.dependencies]
        : existingNode.dependencies,
      linkedDocs: Array.isArray(existingNode.linkedDocs)
        ? [...existingNode.linkedDocs]
        : existingNode.linkedDocs,
      projectId: existingNode.projectId || authoritativeNode.projectId || fallbackProjectId,
    };

    if (JSON.stringify(mergedNode) !== JSON.stringify(existingNode)) {
      changed = true;
    }

    return mergedNode;
  });

  if (!changed) {
    return { data: currentData, changed: false };
  }

  return {
    data: sanitizeProjectData(
      {
        ...currentData,
        nodes: mergedNodes,
      },
      { logRepairs: false }
    ),
    changed: true,
  };
}

function normalizeCreateOperationNodeIdentity(
  currentData: ProjectData,
  operation: NodeOperation,
): void {
  if (operation.type !== 'create') {
    return;
  }

  const idsAreNamespaced = currentData.nodes.some(
    (node) => typeof node.id === 'string' && node.id.includes(':')
  );

  const metadataProjectId = typeof operation.metadata?.projectId === 'string'
    ? operation.metadata.projectId
    : undefined;
  const parentProjectId = typeof operation.parentId === 'string' && operation.parentId.includes(':')
    ? getProjectIdFromNodeId(operation.parentId)
    : undefined;
  const nodeProjectId = typeof operation.data?.projectId === 'string'
    ? operation.data.projectId
    : undefined;
  const inferredProjectId =
    nodeProjectId ||
    metadataProjectId ||
    parentProjectId ||
    (currentData.projectId !== 'default' ? currentData.projectId : undefined);

  const namespaceNodeId = (nodeId: string): string => {
    if (!idsAreNamespaced || !inferredProjectId || nodeId.includes(':')) {
      return nodeId;
    }
    return `${inferredProjectId}:${nodeId}`;
  };

  const requestedNodeId = operation.nodeId || operation.data?.id || `node-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const canonicalNodeId = namespaceNodeId(requestedNodeId);

  operation.nodeId = canonicalNodeId;
  operation.data = {
    ...(operation.data || {}),
    id: canonicalNodeId,
  };
}

/**
 * Hook for node CRUD operations
 */
export function useNodeOperations({
  projectData,
  expandedNodeIds,
  selectedNodeIds,
  filters,
  searchQuery,
  sortBy,
  isCompactView,
  user,
  viewStates,
  uiState,
  sessionId,
  snapshotHistory,
  ws,
  wsManager,
  setProjectData,
  beginUnconfirmedWrite,
  setError,
  updateUndoStateFromSnapshots,
  updateProjectInCache
}: UseNodeOperationsParams) {
  const projectDataRef = useRef<ProjectData | null>(projectData);
  const latestNodeOperationIdsRef = useRef(new Map<string, string>());
  const latestColumnOperationIdsRef = useRef(new Map<string, string>());
  // A generation prevents a pending A completion from becoming current after A→B→A.
  const confirmationPrincipal = getAuthRuntimeSessionIdentity();
  const confirmationViewAs = getAdminViewAsUserId();
  const confirmationSessionId = sessionId.current;
  const confirmationScope = JSON.stringify([
    projectData?.projectId, uiState?.activeView, user?.id, sessionId.current,
    confirmationPrincipal, confirmationViewAs,
  ]);
  const confirmationLifecycleRef = useRef({ scope: confirmationScope, generation: 0, mounted: true });
  if (confirmationLifecycleRef.current.scope !== confirmationScope) {
    confirmationLifecycleRef.current.scope = confirmationScope;
    confirmationLifecycleRef.current.generation += 1;
  }
  const confirmationGeneration = confirmationLifecycleRef.current.generation;
  useEffect(() => {
    const lifecycle = confirmationLifecycleRef.current;
    lifecycle.mounted = true;
    return () => { lifecycle.mounted = false; };
  }, []);
  const latestKanbanReorderGenerationRef = useRef(new Map<string, number>());

  useEffect(() => {
    projectDataRef.current = projectData;
  }, [projectData]);

  useEffect(() => {
    // A browser can become offline while its socket still reports OPEN.
    // Flush that deferred work when online; a replaced socket also flushes
    // through the existing authenticated-acknowledgement path.
    const resumeQueuedOperations = () => webSocketSingleton.flushQueuedOperations();
    window.addEventListener('online', resumeQueuedOperations);
    return () => window.removeEventListener('online', resumeQueuedOperations);
  }, []);

  const showTransientError = useCallback((message: string) => {
    setError(message);
    window.setTimeout(() => setError(null), 5000);
  }, [setError]);

  const updateProjectCacheFromViewData = useCallback((viewData: ProjectData, operation: NodeOperation) => {
    if (!updateProjectInCache) {
      return;
    }

    const affectedProjectIds = new Set<string>();
    const metadataProjectId = typeof operation.metadata?.projectId === 'string'
      ? operation.metadata.projectId
      : null;

    if (metadataProjectId && metadataProjectId !== 'default') {
      affectedProjectIds.add(metadataProjectId.includes(',') ? metadataProjectId.split(',')[0].trim() : metadataProjectId);
    }

    const extractedProjectId = extractProjectIdFromOperation(operation);
    if (extractedProjectId && extractedProjectId !== 'default') {
      affectedProjectIds.add(extractedProjectId);
    }

    if (operation.type === 'batch-delete' && Array.isArray(operation.nodeIds)) {
      operation.nodeIds.forEach((nodeId) => {
        const projectId = getProjectIdFromNodeId(nodeId, viewData.projectId);
        if (projectId && projectId !== 'default') {
          affectedProjectIds.add(projectId);
        }
      });
    }

    if (affectedProjectIds.size === 0 && viewData.projectId) {
      affectedProjectIds.add(viewData.projectId);
    }

    affectedProjectIds.forEach((targetProjectId) => {
      const scopedNodes = viewData.nodes
        .filter((node) => {
          const nodeRecord = node as ProjectNode & Record<string, unknown>;
          if (nodeRecord._deleted || nodeRecord.isDeleted) {
            return false;
          }

          const nodeProjectId = node.projectId || getProjectIdFromNodeId(node.id, viewData.projectId);
          if (nodeProjectId && nodeProjectId !== 'default') {
            return nodeProjectId === targetProjectId;
          }

          return viewData.projectId === targetProjectId;
        })
        .map((node) => ({
          ...node,
          id: getNodeId(node.id),
          parentId: node.parentId ? getNodeId(node.parentId) : node.parentId,
          dependencies: Array.isArray(node.dependencies)
            ? node.dependencies.map((dependencyId: string) => getNodeId(dependencyId))
            : node.dependencies,
        }));

      const rootNodeIds = scopedNodes
        .filter((node) => !node.parentId)
        .sort((left, right) => {
          const leftOrder = typeof left.order === 'number' ? left.order : 0;
          const rightOrder = typeof right.order === 'number' ? right.order : 0;
          return leftOrder - rightOrder;
        })
        .map((node) => node.id);

      updateProjectInCache(targetProjectId, {
        nodes: scopedNodes,
        rootNodeIds,
      });
    });
  }, [updateProjectInCache]);

  const applyOp = useCallback(async (operation: NodeOperation, options?: OperationPersistenceOptions): Promise<void> => {
    // Ordinary callbacks can outlive the render that supplied them (for example,
    // a drag completing after reauthentication or a view-as switch). Fence ingress
    // before history, optimistic writes or transport capture can adopt a new owner.
    // Equality intentionally permits an unchanged unloaded runtime identity and
    // same-owner socket reconnect; confirmed writes retain their stricter checks.
    if (options?.requireConfirmed !== true && (
      getAuthRuntimeSessionIdentity() !== confirmationPrincipal ||
      getAdminViewAsUserId() !== confirmationViewAs
    )) {
      throw new Error('Operation is obsolete: session or view changed');
    }
    const initiatingTransportOwner = webSocketSingleton.getPrincipalContext();
    const historyRestore = operation.type === 'restore-tree';
    const subtreeDelete = operation.type === 'delete-tree';
    const requireConfirmed = options?.requireConfirmed === true || historyRestore || subtreeDelete || Object.prototype.hasOwnProperty.call(operation, 'expectedFields');
    let dispatched = false;
    let explicitlyRejected = false;
    let rejectionCode: string | undefined;
    const initiatingSessionId = confirmationSessionId;
    const initiatingPrincipal = confirmationPrincipal;
    const initiatingViewAs = confirmationViewAs;
    const requestedProjectId = projectDataRef.current?.projectId;
    const originatingRuntimeSession = getAuthRuntimeSessionIdentity();
    const originatingViewAs = getAdminViewAsUserId();
    const isConfirmationCurrent = () => confirmationLifecycleRef.current.mounted &&
      confirmationLifecycleRef.current.generation === confirmationGeneration &&
      sessionId.current === initiatingSessionId &&
      getAuthRuntimeSessionIdentity() === initiatingPrincipal &&
      getAdminViewAsUserId() === initiatingViewAs;
    // PRODUCTION FIX: Comprehensive error handling for all operation processing
    try {
      if (!operation.skipHistory) {
        // Capture a new edit only after grouped undo/redo finishes. Otherwise
        // a later inverse in that group can overwrite the user's newer edit.
        let replay = undoStack.waitForReplay();
        while (replay) {
          await replay;
          if (getAuthRuntimeSessionIdentity() !== originatingRuntimeSession ||
              getAdminViewAsUserId() !== originatingViewAs ||
              projectDataRef.current?.projectId !== requestedProjectId ||
              !confirmationLifecycleRef.current.mounted) {
            throw new Error('Operation is obsolete: session or view changed');
          }
          replay = undoStack.waitForReplay();
        }
      }
      const currentProjectData = projectDataRef.current;

      // Only operations with an acknowledged persistence contract enter this path.
      // Refuse before history, optimistic state, cache updates, or transport queueing.
      if (requireConfirmed) {
        if (!isConfirmationCurrent() || !currentProjectData) {
          throw new Error('Operation is obsolete: session or view changed');
        }
        if (!historyRestore && !subtreeDelete && !(operation.type === 'update' && Object.prototype.hasOwnProperty.call(operation, 'expectedFields')) &&
            !['paste-nodes', 'batch-update', 'add-column', 'update-column', 'delete-column', 'reorder-columns'].includes(operation.type)) {
          throw new Error(`Confirmed persistence is not supported for ${operation.type}`);
        }
        if (!navigator.onLine || !webSocketSingleton.isConnected()) {
          throw new Error('Confirmed persistence requires a ready connection. Change was not sent.');
        }
        if (operation.type === 'batch-update') {
          operation = { ...operation, metadata: { ...operation.metadata, atomic: true } };
        }
      }

      if (!currentProjectData) {
        log.warn('useNodeOperations', 'applyOp called but projectData is null');
        return;
      }

      // Delayed outcomes must remain bound to the principal/view-as session
      // that initiated the mutation.
      const isCurrentPrincipal = () =>
        getAuthRuntimeSessionIdentity() === originatingRuntimeSession &&
        getAdminViewAsUserId() === originatingViewAs;
      const isCurrentView = () => (!requireConfirmed || isConfirmationCurrent()) && isCurrentPrincipal() &&
        projectDataRef.current?.projectId === currentProjectData.projectId;

      normalizeCreateOperationNodeIdentity(currentProjectData, operation);

      // PRODUCTION FIX: Validate operation before processing
      const validationResult = validateNodeOperation(operation, currentProjectData.nodes);
      if (!validationResult.valid) {
        const errorMsg = `Invalid operation: ${validationResult.error}`;
        log.error('useNodeOperations', errorMsg);
        showTransientError(errorMsg);
        throw new Error(errorMsg);
      }

      if (operation.type === 'reorder-columns' && operation.aggregateOrder && operation.skipHistory) {
        const ownerProjectId = operation.metadata?.projectId;
        if (typeof ownerProjectId !== 'string' || !ownerProjectId || !operation.columnOrder?.length) {
          throw new Error(i18n.t('Shared column history requires an owning project and column order'));
        }
        const generation = (latestKanbanReorderGenerationRef.current.get(ownerProjectId) || 0) + 1;
        latestKanbanReorderGenerationRef.current.set(ownerProjectId, generation);
        const readColumns = async () => {
          const response = await authService.authenticatedFetch(
            `${API_BASE_URL}/api/v1/projects/${ownerProjectId}/kanban/columns`, { method: 'GET' }
          );
          if (!response.ok) throw new Error(i18n.t('Column history readback failed'));
          const payload = await response.json();
          if (!Array.isArray(payload?.data?.columns) || payload.data.columns.length === 0) throw new Error(i18n.t('Column history readback is incomplete'));
          const saved = payload.data.columns.map(normalizeKanbanColumn) as KanbanColumnConfig[];
          if (isCurrentPrincipal() && latestKanbanReorderGenerationRef.current.get(ownerProjectId) === generation) {
            updateProjectInCache?.(ownerProjectId, { kanbanColumns: saved });
            if (projectDataRef.current?.projectId === ownerProjectId && isCurrentView()) {
              const next = { ...projectDataRef.current, kanbanColumns: saved };
              projectDataRef.current = next;
              setProjectData(next);
            }
          }
          return saved;
        };
        try {
          const response = await authService.authenticatedFetch(
            `${API_BASE_URL}/api/v1/projects/${ownerProjectId}/kanban/columns/reorder`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ column_order: operation.columnOrder, aggregate_order: operation.aggregateOrder }),
            }
          );
          if (!response.ok) throw new Error(i18n.t('Column history was rejected by the server'));
          const saved = await readColumns();
          const owned = new Set(saved.map(column => column.id));
          const expected = [...new Set(operation.columnOrder.map(normalizeKanbanColumnId))].filter(id => owned.has(id));
          if (JSON.stringify(saved.filter(column => expected.includes(column.id)).map(column => column.id)) !== JSON.stringify(expected) ||
            saved.some(column => Object.prototype.hasOwnProperty.call(operation.aggregateOrder, column.id) && (column.aggregateOrder ?? null) !== operation.aggregateOrder![column.id])) {
            throw new Error(i18n.t('Column history readback did not retain the requested order'));
          }
          if (!isCurrentPrincipal()) throw new Error(i18n.t('Column history belongs to the previous session'));
        } catch (error) {
          if (isCurrentPrincipal()) await readColumns().catch(() => undefined);
          throw error;
        }
        return;
      }

      // Generate unique operation ID and enrich with before state (using extracted utilities)
      const operationWithId = ensureOperationId(operation);
      const enrichedOperation = enrichOperationWithBeforeState(operationWithId, currentProjectData);
      const operationWithIdRecord = operationWithId as NodeOperation & { id?: string };
      if (requireConfirmed && !operationWithIdRecord.id) {
        throw new Error('Confirmed persistence requires an operation ID');
      }

      // Generate meaningful description if not provided (using extracted utility)
      if (!enrichedOperation.description) {
        enrichedOperation.description = generateOperationDescription(operation);
      }

      if (operation.type === 'update' && ['document-http', 'report-http'].includes(String(operation.metadata?.persistence))) {
        const reportPersistence = operation.metadata?.persistence === 'report-http';
        const documentProjectId = typeof operation.metadata?.projectId === 'string'
          ? operation.metadata.projectId : extractProjectIdFromOperation(operation);
        if (!documentProjectId || documentProjectId === 'default' || !operation.nodeId) {
          throw new Error('Note updates require an explicit project and document');
        }
        if (operation.nodeId.includes(':') && getProjectIdFromNodeId(operation.nodeId) !== documentProjectId) {
          throw new Error('The note identifier belongs to a different project');
        }
        const targets = currentProjectData.nodes.filter(node => getNodeId(node.id) === getNodeId(operation.nodeId!)
          && (node.projectId || getProjectIdFromNodeId(node.id) || currentProjectData.projectId) === documentProjectId);
        if (targets.length !== 1 || targets[0].type !== 'document') {
          throw new Error('The note update requires one visible document in its owning project');
        }
        if (reportPersistence && getNodeCustomField(targets[0], 'isReport', 'is_report') !== true) {
          throw new Error('Report history requires a visible report in its owning project');
        }
        enrichedOperation.beforeState = structuredClone(targets[0]);
        const originatingSession = getAuthRuntimeSessionIdentity();
        const originatingViewAs = getAdminViewAsUserId();

        const confirmWrite = beginUnconfirmedWrite?.(JSON.stringify(['http-document', documentProjectId, getNodeId(operation.nodeId), reportPersistence, operation.data]));
        const document = reportPersistence
          ? await (async () => {
            const expected = operation.metadata?.expectedReportBody;
            if (!isReportBody(expected)) throw new Error('Report history is missing its original body');
            return persistReportDocumentBody(documentProjectId, getNodeId(operation.nodeId!), readReportBody(operation.data || {}), expected, () => {
              if (!isCurrentPrincipal()) throw new Error('Session changed while saving the report');
            });
          })()
          : await persistNoteDocumentUpdate(documentProjectId, operation.nodeId, operation.data || {});
        if (!document) { confirmWrite?.(); return; }
        if (getAuthRuntimeSessionIdentity() !== originatingSession || getAdminViewAsUserId() !== originatingViewAs) return;
        const latestData = projectDataRef.current;
        if (latestData) {
          const merged = mergeAuthoritativeNodesIntoProjectData(latestData, [document], documentProjectId);
          if (merged.changed) {
            projectDataRef.current = merged.data;
            setProjectData(merged.data);
            updateProjectCacheFromViewData(merged.data, operation);
          }
        }
        confirmWrite?.();
        // History represents the acknowledged write and keeps its HTTP route for undo/redo.
        if (!operation.skipHistory) undoStack.addOperation(enrichedOperation);
        return;
      }
    
    // Add to undo stack for NodeOperations too (unless skipHistory is true)
    if (!operation.skipHistory && !subtreeDelete && !Object.prototype.hasOwnProperty.call(operation, 'expectedFields')) {
      try {
        undoStack.addOperation(enrichedOperation);
      } catch {
        // Silent fail - undo stack is best-effort
      }
    }
    
    // Apply the operation to get new state
    // For create operations in multi-project mode, we need to use the correct projectId
    let newData: ProjectData;
    const previousProjectData = currentProjectData;

    // Keep date-based Gantt group shifts responsive in the initiating client.
    // Structural group moves still wait for the server's canonical sibling order.
    if (subtreeDelete || historyRestore || (operation.type === 'group-move' && operation.daysDelta === undefined)) {
      log.debug('useNodeOperations', 'Skipping optimistic update for structural group-move, waiting for server result');
      newData = currentProjectData; // Keep current state, don't apply locally
    } else if (operation.type === 'create' && operation.parentId && operation.parentId.includes(':')) {
      // Extract the correct projectId from the namespaced parentId
      const extractedProjectId = getProjectIdFromNodeId(operation.parentId);

      // Create a modified projectData with the correct projectId for this operation
      const projectDataForCreate = {
        ...currentProjectData,
        projectId: extractedProjectId,
        projectName: extractedProjectId // Use projectId as name temporarily
      };


      // Apply with corrected project data
      newData = applyOperation(projectDataForCreate, operation);
      // But restore the original projectId to avoid contamination
      newData.projectId = currentProjectData.projectId;
      newData.projectName = currentProjectData.projectName;
    } else {
      newData = applyOperation(currentProjectData, operation);
    }
    
    // Keep history tied to the requested canonical ID. A parent-relative create
    // can be inserted before unrelated nodes in the flat project array.
    if (operation.type === 'create') {
      synchronizeCreateHistoryIdentity(operation, enrichedOperation, newData);
    }

    const isNodeMutationOperation =
      operation.type === 'create' ||
      operation.type === 'update' ||
      operation.type === 'delete' ||
      operation.type === 'delete-tree' ||
      operation.type === 'restore-tree' ||
      operation.type === 'move' ||
      operation.type === 'reorder' ||
      operation.type === 'batch-update' ||
      operation.type === 'batch-delete' ||
      operation.type === 'group-move' ||
      operation.type === 'progress-update';

    if (operationWithIdRecord.id && isNodeMutationOperation) {
      markLatestNodeOperation(
        latestNodeOperationIdsRef.current,
        operation,
        operationWithIdRecord.id,
        previousProjectData.projectId
      );
    }
    
    
    // Add snapshot to history (unless it's a history operation)
    if (!operation.skipHistory && !subtreeDelete) {
      // Create full application snapshot
      const applicationSnapshot: ApplicationSnapshot = {
        projectData: newData,
        viewStates: viewStates || {
          gantt: {} as ViewStates['gantt'],
          kanban: {} as ViewStates['kanban'],
          calendar: {} as ViewStates['calendar'],
          dashboard: {} as ViewStates['dashboard'],
          documents: {} as ViewStates['documents'],
          reports: {} as ViewStates['reports'],
          settings: {} as ViewStates['settings']
        },
        uiState: uiState || {
          activeView: 'dashboard',
          sidebarCollapsed: false,
          sidebarWidth: 280,
          theme: 'system',
          recentlyUsedEmojis: [],
          commandPaletteOpen: false,
          notificationsPanelOpen: false,
          globalSearchQuery: '',
          pinnedViews: []
        },
        treeState: {
          expandedNodeIds: Array.from(expandedNodeIds),
          selectedNodeIds: Array.from(selectedNodeIds),
          scrollPosition: 0
        },
        filterState: {
          filters,
          searchQuery,
          sortBy,
          isCompactView
        },
        metadata: {
          timestamp: Date.now(),
          version: '1.0.0',
          sessionId: sessionId.current,
          userId: user?.id
        }
      };
      
      // Determine what changed
      const changes = {
        hasDataChanges: true, // This is a data operation
        hasViewStateChanges: false,
        hasUIStateChanges: false,
        affectedFeatures: []
      };
      
      snapshotHistory.current.addSnapshot(applicationSnapshot, enrichedOperation, changes);
      
      // Update undo state after adding snapshot
      updateUndoStateFromSnapshots();
      
      // Debug logging - commented out to reduce console noise
      // if (process.env.NODE_ENV === 'development') {
      //   const positionInfo = snapshotHistory.current.getPositionInfo();
      //   log.debug('useNodeOperations', '[ProjectDataContext] After operation:', {
      //     operation: enrichedOperation.type,
      //     snapshots: positionInfo.total,
      //     currentIndex: positionInfo.current - 1,
      //     canUndo: snapshotHistory.current.canUndo(),
      //     canRedo: snapshotHistory.current.canRedo()
      //   });
      // }
    }
    
    // CRITICAL FIX: Track operation BEFORE applying locally to prevent double-application
    // Pass session ID to ensure we only skip our own echoes
    const enrichedOpRecord = enrichedOperation as NodeOperation & { id?: string };
    if (enrichedOpRecord.id && wsManager?.current) {
      // Track with our session ID so we can identify our own operations
      const ourSessionId = sessionId.current;
      webSocketSingleton.trackOperation(enrichedOpRecord.id, ourSessionId);
      log.debug('useNodeOperations', 'Tracked operation BEFORE local apply:', {
        operationId: enrichedOpRecord.id,
        sessionId: ourSessionId,
        type: enrichedOpRecord.type
      });
    }

    const confirmWrite = operation.type === 'copy-nodes' || operation.type === 'cut-nodes'
      ? undefined : beginUnconfirmedWrite?.(!requireConfirmed && operation.type === 'reorder-columns'
        ? JSON.stringify(['http-column-order', currentProjectData.projectId, operation.metadata?.projectId, operation.columnOrder]) : undefined);
    projectDataRef.current = newData;
    setProjectData(newData);

    if (isNodeMutationOperation) {
      updateProjectCacheFromViewData(newData, operation);
    }

    const isKanbanColumnOperation =
      operation.type === 'add-column' ||
      operation.type === 'update-column' ||
      operation.type === 'delete-column' ||
      operation.type === 'reorder-columns';

    const columnOperationKey = isKanbanColumnOperation
      ? JSON.stringify([operation.metadata?.projectId || currentProjectData.projectId,
        normalizeKanbanColumnId(operation.columnId || operation.columnData?.id || '')])
      : undefined;
    if (columnOperationKey && operationWithIdRecord.id) {
      latestColumnOperationIdsRef.current.set(columnOperationKey, operationWithIdRecord.id);
    }

    if (isKanbanColumnOperation && updateProjectInCache) {
      const metadataProjectId = operation.metadata?.projectId;
      const cacheProjectId =
        (typeof metadataProjectId === 'string' ? metadataProjectId : undefined) ||
        extractProjectIdFromOperation(operation) ||
        currentProjectData.projectId;

      if (cacheProjectId) {
        updateProjectInCache(cacheProjectId, {
          kanbanColumns: newData.kanbanColumns,
          ...(operation.type === 'delete-column' ? { nodes: newData.nodes } : {})
        });
      }
    }

    // NEW: Auto-pin the edited project's ID so the UI follows the user action
    try {
      const windowGlobal = window as unknown as Record<string, unknown>;
      const mp = windowGlobal.__multiProjectContext as { setActiveProject?: (id: string|null)=>void } | undefined;
      let editedProjectId: string | null = null;
      if (operation.nodeId && operation.nodeId.includes(':')) editedProjectId = getProjectIdFromNodeId(operation.nodeId);
      else if (operation.parentId && operation.parentId.includes(':')) editedProjectId = getProjectIdFromNodeId(operation.parentId);
      if (mp?.setActiveProject && editedProjectId) {
        mp.setActiveProject(editedProjectId);
      }
    } catch { /* best-effort auto-pin */ }
    
    // Simplified: rely on server refresh via external-change WS event; avoid pushing live node arrays to cache
    // This prevents duplicate crash from malformed node IDs in the cache update filter
    // Previously extracted project ID here, but no longer needed with server refresh approach

    const metadataProjectId = operation.metadata?.projectId;
    const kanbanProjectId =
      (typeof metadataProjectId === 'string' ? metadataProjectId : undefined) ||
      extractProjectIdFromOperation(operation) ||
      currentProjectData.projectId;

    if (!requireConfirmed && operation.type === 'reorder-columns' && kanbanProjectId && Array.isArray(operation.columnOrder)) {
      const normalizedColumnOrder = operation.columnOrder.map((columnId) =>
        normalizeKanbanColumnId(String(columnId))
      );
      const optimisticHistoryEntryId = operation.metadata?.optimisticHistoryEntryId as string | undefined;
      const reorderGeneration = (latestKanbanReorderGenerationRef.current.get(kanbanProjectId) || 0) + 1;
      latestKanbanReorderGenerationRef.current.set(kanbanProjectId, reorderGeneration);

      void authService.authenticatedFetch(
        `${API_BASE_URL}/api/v1/projects/${kanbanProjectId}/kanban/columns/reorder`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ column_order: normalizedColumnOrder, aggregate_order: operation.aggregateOrder }),
        }
      ).then(async (response) => {
        if (!response.ok) {
          throw new Error('Failed to sync Kanban column reorder');
        }
        const readback = await authService.authenticatedFetch(
          `${API_BASE_URL}/api/v1/projects/${kanbanProjectId}/kanban/columns`, { method: 'GET' }
        );
        if (!readback.ok) throw new Error('Column order readback failed');
        const payload = await readback.json();
        const savedOrder = Array.isArray(payload?.data?.columns)
          ? payload.data.columns.map((column: { id?: string }) => normalizeKanbanColumnId(String(column.id || ''))) : null;
        if (JSON.stringify(savedOrder) !== JSON.stringify(normalizedColumnOrder)) throw new Error('Column order readback did not match the write');
        if (operation.aggregateOrder && payload.data.columns.some((column: { id: string; aggregate_order?: number | null; aggregateOrder?: number | null }) =>
          Object.prototype.hasOwnProperty.call(operation.aggregateOrder, column.id) && (column.aggregate_order ?? column.aggregateOrder ?? null) !== operation.aggregateOrder![column.id])) {
          throw new Error(i18n.t('Aggregate column order readback did not match the write'));
        }
        if (isCurrentPrincipal()) confirmWrite?.();
      }).catch(async () => {
        if (
          getAuthRuntimeSessionIdentity() !== originatingRuntimeSession ||
          getAdminViewAsUserId() !== originatingViewAs
        ) {
          return;
        }

        if (optimisticHistoryEntryId) {
          undoStack.removeEntry(optimisticHistoryEntryId);
        }

        if (latestKanbanReorderGenerationRef.current.get(kanbanProjectId) !== reorderGeneration) {
          return;
        }

        try {
          const response = await authService.authenticatedFetch(
            `${API_BASE_URL}/api/v1/projects/${kanbanProjectId}/kanban/columns`,
            { method: 'GET' }
          );
          if (!response.ok) {
            throw new Error(`Column readback failed (${response.status})`);
          }
          const payload = await response.json();
          if (!Array.isArray(payload?.data?.columns)) {
            throw new Error('Column readback is incomplete');
          }
          if (
            latestKanbanReorderGenerationRef.current.get(kanbanProjectId) !== reorderGeneration ||
            getAuthRuntimeSessionIdentity() !== originatingRuntimeSession ||
            getAdminViewAsUserId() !== originatingViewAs
          ) {
            return;
          }
          const authoritativeColumns = payload.data.columns.map(normalizeKanbanColumn);
          const latestProjectData = projectDataRef.current;
          if (latestProjectData && isCurrentView()) {
            const reconciledProjectData = { ...latestProjectData, kanbanColumns: authoritativeColumns };
            projectDataRef.current = reconciledProjectData;
            setProjectData(reconciledProjectData);
          }
          updateProjectInCache?.(kanbanProjectId, { kanbanColumns: authoritativeColumns });
          if (isCurrentView()) {
            // The handler has replaced this intent with a completed canonical readback.
            confirmWrite?.();
            showTransientError('Column reorder failed. Showing current server order.');
          }
        } catch {
          if (isCurrentView()) showTransientError('Column order could not be confirmed. Reload the project to verify current server state.');
        }
      });

      return;
    }

    // Clipboard selection is local UI state, not a project mutation requiring a receipt.
    if (operation.type === 'copy-nodes' || operation.type === 'cut-nodes') return;

    // Send operation to server with session ID.
    // The singleton is the actual mutation transport, so dispatch and ack
    // behavior must follow its connection state rather than the legacy refs.
    if (initiatingTransportOwner.userId !== user?.id || !webSocketSingleton.isCurrentPrincipal(initiatingTransportOwner)) {
      throw new Error('Operation principal changed before transport dispatch');
    }
    const singletonConnected = webSocketSingleton.isConnected();
    const dispatchMode = resolveRealtimeDispatchMode({
      isOnline: navigator.onLine,
      websocketConnected: singletonConnected,
    });

    // Build server operation before syncing so reconnect-path dispatch can reuse
    // the same canonical payload whether the socket is live or still reopening.
    const { serverOperation, projectId } = buildServerOperation(
      operationWithId,
      previousProjectData,
      sessionId.current,
      operationWithIdRecord.id
    );

    // Ensure project ID is set on server operation (using extracted utility)
    ensureProjectId(serverOperation, projectId, previousProjectData, operationWithId);
    if (
      (serverOperation.type || operation.type) === 'group-move' &&
      (
        typeof serverOperation.projectId !== 'string' ||
        !serverOperation.projectId ||
        serverOperation.projectId === 'default'
      )
    ) {
      throw new Error('Cannot send group-move without explicit project context');
    }

    if (requireConfirmed && (dispatchMode !== 'online-ack' || !isConfirmationCurrent())) {
      throw new Error('Operation could not be confirmed: connection or view changed before sending');
    }

    if (dispatchMode !== 'offline-queued') {
      try {
        // Use singleton WebSocket for all operations
        if (dispatchMode === 'online-queued' && user?.id) {
          const queuedProjectId =
            (serverOperation.projectId as string | undefined) ||
            previousProjectData.projectId;
          if (queuedProjectId) {
            webSocketSingleton.maintainConnection(user.id, queuedProjectId, initiatingTransportOwner);
          }
        }
        if (webSocketSingleton.isConnected()) {
          // Track that we're sending an operation (for echo detection)
          (window as unknown as Record<string, unknown>).__lastOperationSent = Date.now();

          // Receipts may arrive while durable admission resolves. Publish the
          // tracker before that await so early confirmations remain observable.
          (window as unknown as Record<string, unknown>).webSocketSingleton = webSocketSingleton;
          // Mark before send: a transport exception cannot prove the write never left.
          try {
            await webSocketSingleton.sendOperation(serverOperation, serverOperation.projectId as string | undefined, { owner: initiatingTransportOwner, onCommitted: confirmWrite });
            dispatched = true;
          } catch (error) {
            dispatched = error instanceof OperationJournalError && error.admitted;
            throw error;
          }
          if (!isCurrentView() || !webSocketSingleton.isCurrentPrincipal(initiatingTransportOwner)) {
            if (requireConfirmed) throw new OperationJournalError('Operation was admitted for the previous view; its outcome remains retained', true);
            return;
          }
          // ACK gating: if this op depends on persistence (update/move/reorder/group-move/create), wait briefly
          const shouldGate = requireConfirmed || ['create','update','move','reorder','group-move','batch-update'].includes((serverOperation.type || operation.type) as string);
          if (dispatchMode === 'online-ack' && shouldGate && operationWithIdRecord.id) {
            try {
              // Expose singleton to window for WebSocketManager to notify
              (window as unknown as Record<string, unknown>).webSocketSingleton = webSocketSingleton;
            } catch { /* best-effort window exposure */ }
            const rawResult = await webSocketSingleton.waitForOperation(operationWithIdRecord.id!, 3000);
            if (requireConfirmed && (!isConfirmationCurrent() || !isCurrentView())) {
              throw new Error('Operation is obsolete: session or view changed while awaiting confirmation');
            }
            if (!isCurrentView()) return;
            if (requireConfirmed && (
              !isLatestNodeOperationAck(latestNodeOperationIdsRef.current, operation,
                operationWithIdRecord.id, [], projectId || previousProjectData.projectId) ||
              (columnOperationKey && latestColumnOperationIdsRef.current.get(columnOperationKey) !== operationWithIdRecord.id)
            )) {
              throw new Error('Operation is obsolete: a newer local mutation superseded its acknowledgement');
            }
            const operationResult = rawResult as Record<string, unknown> | undefined;
            const opResultResult = operationResult?.result as Record<string, unknown> | undefined;
            const operationFailed =
              operationResult?.type === 'operation-error' ||
              opResultResult?.success === false ||
              (requireConfirmed && operationResult?.success === false);

            if (operationFailed) {
              const code = operationResult?.code ?? opResultResult?.code;
              explicitlyRejected = code !== 'operation_outcome_unknown' &&
                operationResult?.outcome !== 'unknown' && opResultResult?.outcome !== 'unknown';
              if (typeof code === 'string') rejectionCode = code;
              const ackStillMatchesLatestLocalNodeMutation = isLatestNodeOperationAck(
                latestNodeOperationIdsRef.current,
                operation,
                operationWithIdRecord.id,
                [],
                (serverOperation.projectId as string | undefined) || previousProjectData.projectId
              );

              if (!ackStillMatchesLatestLocalNodeMutation) {
                log.warn('useNodeOperations', 'Ignoring stale rejected acknowledgement for an older node mutation', {
                  operationId: operationWithIdRecord.id,
                  operationType: operation.type,
                });
                return;
              }

              if (
                getAuthRuntimeSessionIdentity() !== originatingRuntimeSession ||
                getAdminViewAsUserId() !== originatingViewAs
              ) {
                return;
              }

              const rejectedProjectId =
                (serverOperation.projectId as string | undefined) ||
                previousProjectData.projectId;
              if (
                rejectedProjectId &&
                (operation.type === 'update' || operation.type === 'batch-update')
              ) {
                try {
                  const response = await authService.authenticatedFetch(
                    `${API_BASE_URL}/api/v1/projects/${rejectedProjectId}`,
                    { method: 'GET' }
                  );
                  if (response.ok) {
                    const payload = await response.json();
                    const rawProject = payload?.data?.project || payload?.project || payload?.data || payload;
                    if (rawProject && typeof rawProject === 'object' && isCurrentView()) {
                      const authoritativeProjectData = sanitizeProjectData(
                        transformProjectDataFromBackend(rawProject)
                      );
                      const affectedNodeIds = new Set<string>();
                      if (operation.nodeId) {
                        affectedNodeIds.add(getNodeId(operation.nodeId));
                      }
                      operation.updates?.forEach((update) => affectedNodeIds.add(getNodeId(update.nodeId)));
                      const authoritativeNodes = authoritativeProjectData.nodes.filter(
                        (node) => affectedNodeIds.has(getNodeId(node.id))
                      );
                      const ackStillCurrent = isLatestNodeOperationAck(
                        latestNodeOperationIdsRef.current,
                        operation,
                        operationWithIdRecord.id,
                        authoritativeNodes,
                        rejectedProjectId
                      );
                      if (
                        ackStillCurrent &&
                        getAuthRuntimeSessionIdentity() === originatingRuntimeSession &&
                        getAdminViewAsUserId() === originatingViewAs &&
                        projectDataRef.current && isCurrentView()
                      ) {
                        const reconciled = mergeAuthoritativeNodesIntoProjectData(
                          projectDataRef.current,
                          authoritativeNodes,
                          rejectedProjectId
                        );
                        if (reconciled.changed) {
                          projectDataRef.current = reconciled.data;
                          setProjectData(reconciled.data);
                          updateProjectCacheFromViewData(reconciled.data, operation);
                        }
                      }
                    }
                  }
                } catch (readbackError) {
                  log.warn('useNodeOperations', 'Failed to read current project after rejected operation', readbackError);
                }
              }
              throw Object.assign(new Error(
                (operationResult?.error as string) ||
                (operationResult?.message as string) ||
                (opResultResult?.error as string) ||
                'Server rejected operation'
              ), { code: rejectionCode });
            }

            if (requireConfirmed && (
              !['operation-confirmed', 'operation-result'].includes(String(operationResult?.type)) ||
              opResultResult?.success !== true || operationResult?.queued === true || opResultResult?.queued === true
            )) {
              throw new Error('Operation was not confirmed by the server (missing acknowledgement, timeout, or queued outcome)');
            }

            const operationMetadata = serverOperation.metadata as Record<string, unknown> | undefined;
            const shouldRefreshAuthoritativeCascade =
              (serverOperation.type || operation.type) === 'update' &&
              Boolean(
                operationMetadata?.applyToDescendants ||
                operationMetadata?.apply_to_descendants
              );
            const operationType = String(serverOperation.type || operation.type || '');
            const authoritativeProjectId =
              (serverOperation.projectId as string | undefined) ||
              projectDataRef.current?.projectId ||
              previousProjectData.projectId;
            const authoritativeNodes = extractAuthoritativeNodesFromOperationResult(opResultResult);
            const ackStillMatchesLatestLocalNodeMutation = isLatestNodeOperationAck(
              latestNodeOperationIdsRef.current,
              operation,
              operationWithIdRecord.id,
              authoritativeNodes,
              authoritativeProjectId
            );
            if (requireConfirmed && !ackStillMatchesLatestLocalNodeMutation) {
              throw new Error('Operation is obsolete: a newer local mutation superseded its acknowledgement');
            }
            if (subtreeDelete && projectDataRef.current) {
              const ids = opResultResult?.deletedNodeIds;
              const deleted = opResultResult?.deletedNodes;
              if (!Array.isArray(ids) || !ids.length || !Array.isArray(deleted) || ids.length !== deleted.length) {
                throw new Error('Subtree deletion receipt is incomplete. Reload to verify the saved result.');
              }
              const namespaced = previousProjectData.nodes.some(node => node.id.includes(':'));
              const deletedNodes = deleted.map(raw => transformNodeFromBackend(raw)).map(node =>
                normalizeOperationIds({data:node}, namespaced, authoritativeProjectId).data as ProjectNode);
              const root = deletedNodes.find(node => getNodeId(node.id) === getNodeId(operation.nodeId || ''));
              if (!root) throw new Error('Subtree deletion receipt is missing its root.');
              const receiptOperation = normalizeOperationIds({type:'delete-tree',nodeId:operation.nodeId,
                deletedNodeIds:ids,updatedNodes:authoritativeNodes.map(node=>({nodeId:node.id,node}))},
                namespaced,authoritativeProjectId) as unknown as NodeOperation;
              const next = applyOperation(projectDataRef.current, receiptOperation);
              projectDataRef.current = next;
              setProjectData(next);
              updateProjectCacheFromViewData(next, operation);
              enrichedOperation.deletedTree = {root,descendants:deletedNodes.filter(node=>node.id!==root.id)};
              enrichedOperation.deletedNode = root;
              operation.sourceOperationId = operationWithIdRecord.id;
              enrichedOperation.sourceOperationId = operationWithIdRecord.id;
              if (!operation.skipHistory) undoStack.addOperation(enrichedOperation);
            }
            if (historyRestore && projectDataRef.current) {
              const restored = normalizeOperationIds({type:'batch-update',updatedNodes:authoritativeNodes.map(node=>({nodeId:node.id,node}))},
                operation.nodeId?.includes(':') === true || projectDataRef.current.nodes.some(node=>node.id.includes(':')),
                authoritativeProjectId) as unknown as NodeOperation;
              const next = applyOperation(projectDataRef.current, restored);
              projectDataRef.current = next;
              setProjectData(next);
              updateProjectCacheFromViewData(next, operation);
            }
            if (Object.prototype.hasOwnProperty.call(operation, 'expectedFields') && !operation.skipHistory) {
              undoStack.addOperation(enrichedOperation);
            }
            const shouldMergeAckOnlyAuthoritativeResult =
              !shouldRefreshAuthoritativeCascade &&
              isAckOnlySameSessionOperationType(operationType) &&
              authoritativeNodes.length > 0 &&
              ackStillMatchesLatestLocalNodeMutation;

            if (shouldMergeAckOnlyAuthoritativeResult && projectDataRef.current) {
              const authoritativeMerge = mergeAuthoritativeNodesIntoProjectData(
                projectDataRef.current,
                authoritativeNodes,
                authoritativeProjectId
              );

              if (authoritativeMerge.changed) {
                log.debug('useNodeOperations', 'Merged same-session authoritative ack in place', {
                  operationType,
                  nodeCount: authoritativeNodes.length,
                });
                projectDataRef.current = authoritativeMerge.data;
                setProjectData(authoritativeMerge.data);
                updateProjectCacheFromViewData(authoritativeMerge.data, operation);
              }
            }

            if (shouldRefreshAuthoritativeCascade && ackStillMatchesLatestLocalNodeMutation) {
              if (authoritativeNodes.length > 0 && projectDataRef.current) {
                const authoritativeCascadeMerge = mergeAuthoritativeNodesIntoProjectData(
                  projectDataRef.current,
                  authoritativeNodes,
                  authoritativeProjectId
                );

                if (authoritativeCascadeMerge.changed) {
                  log.debug('useNodeOperations', 'Merged cascade ack subtree in place', {
                    nodeCount: authoritativeNodes.length,
                    projectId: authoritativeProjectId,
                  });
                  projectDataRef.current = authoritativeCascadeMerge.data;
                  setProjectData(authoritativeCascadeMerge.data);
                  updateProjectCacheFromViewData(authoritativeCascadeMerge.data, operation);
                }
              } else if (authoritativeProjectId) {
                log.warn('useNodeOperations', 'Cascade ack missing updatedNodesFull; falling back to targeted project refresh', {
                  operationType,
                  projectId: authoritativeProjectId,
                });
                try {
                  const response = await authService.authenticatedFetch(
                    `${API_BASE_URL}/api/v1/projects/${authoritativeProjectId}`
                  );
                  if (response.ok) {
                    const payload = await response.json();
                    const rawProject =
                      payload?.data?.project ||
                      payload?.project ||
                      payload?.data ||
                      payload;
                    if (rawProject && typeof rawProject === 'object' && isCurrentView()) {
                      const authoritativeProjectData = sanitizeProjectData(
                        transformProjectDataFromBackend(rawProject)
                      );
                      projectDataRef.current = authoritativeProjectData;
                      setProjectData(authoritativeProjectData);
                      updateProjectCacheFromViewData(authoritativeProjectData, operation);
                    }
                  }
                } catch (refreshError) {
                  log.warn('useNodeOperations', 'Failed to refresh project after cascade update', refreshError);
                }
              }
            }
          } else if (dispatchMode === 'online-queued') {
            showTransientError('Reconnecting to server. Change queued for sync.');
          }
        } else {
          if (requireConfirmed) throw new Error('Connection lost before sending; operation was not confirmed');
          await webSocketSingleton.sendOperation(serverOperation, serverOperation.projectId as string | undefined, { owner: initiatingTransportOwner, onCommitted: confirmWrite });
          if (!isCurrentView()) return;
          showTransientError('Reconnecting to server. Change queued for sync.');
        }
      } catch (syncError) {
        if (requireConfirmed || syncError instanceof OperationJournalError) throw syncError;
        if (!isCurrentView()) return;
        const syncErrorMessage = syncError instanceof Error ? syncError.message : String(syncError);
        const language = (i18n.resolvedLanguage || i18n.language || '').startsWith('ko') ? 'ko' : 'en';
        const isPermissionRejection =
          /permission|not have access|not authorized|403/i.test(syncErrorMessage);
        const unconfirmedMessage = language === 'ko'
          ? '서버와 변경 내용을 동기화하지 못했습니다. 현재 저장 상태를 확인한 후 다시 시도하세요.'
          : 'Failed to sync changes with server. Current local outcome is unconfirmed.';
        showTransientError(
          rejectionCode || isPermissionRejection
            ? presentServerError(syncError, language, unconfirmedMessage)
            : unconfirmedMessage
        );

        try {
          const msg = syncErrorMessage;
          if (msg.includes('not found in project')) {
            const pid = (serverOperation.projectId as string | undefined) || projectData?.projectId;
            if (pid) {
              const windowGlobalInner = window as unknown as Record<string, unknown>;
              const multiCtx = windowGlobalInner.__multiProjectContext as
                | { reloadProject?: (id: string) => void }
                | undefined;
              if (multiCtx?.reloadProject) {
                multiCtx.reloadProject(pid);
              }
              const nid = serverOperation.nodeId as string | undefined;
              if (nid) {
                try {
                  setProjectData(prev => {
                    if (!prev || !Array.isArray(prev.nodes)) return prev;
                    const nodes = prev.nodes.map(n => n.id === nid ? { ...n, _deleted: true, isDeleted: true } : n);
                    return { ...prev, nodes };
                  });
                } catch { /* best-effort node deletion */ }
              }
            }
          }
        } catch { /* best-effort error recovery */ }
      }
    } else {
      await webSocketSingleton.sendOperation(
        serverOperation,
        serverOperation.projectId as string | undefined,
        { queueOnly: true, owner: initiatingTransportOwner, onCommitted: confirmWrite },
      );
      if (!isCurrentView()) return;
      showTransientError("Offline: Change queued for sync when the connection returns.");
    }
    } catch (topLevelError) {
      // PRODUCTION FIX: Catch all errors including those from enrichment logic
      const language = (i18n.resolvedLanguage || i18n.language || '').startsWith('ko') ? 'ko' : 'en';
      const detail = presentServerError(topLevelError, language,
        topLevelError instanceof Error ? topLevelError.message : String(topLevelError));
      const outcomeText = language === 'ko'
        ? explicitlyRejected ? '서버에서 요청을 거부하여 적용하지 않았습니다.' : dispatched
          ? '변경 내용이 저장되었을 수 있습니다. 다시 시도하기 전에 현재 서버 상태를 확인하세요.'
          : '변경 요청을 전송하지 않았습니다.'
        : explicitlyRejected ? 'Server rejected the write.' : dispatched
          ? 'Write was sent and may have committed. Verify current server state before retrying.'
          : 'Change was not dispatched.';
      const errorMessage = requireConfirmed
        ? `${outcomeText} ${detail}`
        : detail;
      log.error('useNodeOperations', 'Operation failed with error:', topLevelError);

      // Show user-friendly error message
      if (!requireConfirmed || isConfirmationCurrent()) showTransientError(`${language === 'ko' ? '작업 실패' : 'Operation failed'}: ${errorMessage}`);

      // Re-throw to ensure callers can handle if needed
      if (requireConfirmed) throw new ConfirmedWriteError(errorMessage,
        explicitlyRejected ? 'rejected' : dispatched ? 'unknown' : 'not-dispatched', rejectionCode);
      throw topLevelError;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- projectData is accessed via projectDataRef to avoid stale closures; updateProjectInCache is captured by updateProjectCacheFromViewData
  }, [confirmationGeneration, expandedNodeIds, selectedNodeIds, filters, searchQuery, sortBy, isCompactView, user, viewStates, uiState, sessionId, snapshotHistory, ws, wsManager, setProjectData, beginUnconfirmedWrite, showTransientError, updateUndoStateFromSnapshots, updateProjectCacheFromViewData]);

  // Clipboard operations (using extracted utilities)
  const copyNodes = useCallback(async (nodeIds: string[]) => {
    const currentProjectData = projectDataRef.current;
    if (!currentProjectData) return;
    const operation = await createCopyOperation(currentProjectData, nodeIds);
    if (operation) await applyOp(operation);
  }, [applyOp]);
  
  const cutNodes = useCallback(async (nodeIds: string[]) => {
    const currentProjectData = projectDataRef.current;
    if (!currentProjectData) return;
    const operation = await createCutOperation(currentProjectData, nodeIds);
    if (operation) await applyOp(operation);
  }, [applyOp]);
  
  const pasteNodes = useCallback(async (parentId: string, index?: number) => {
    const currentProjectData = projectDataRef.current;
    if (!currentProjectData) return;

    const { operation, shouldClearClipboard, clipboardRevision } = await createPasteOperation(currentProjectData, parentId, index);
    if (!operation) return;

    const requireConfirmed = navigator.onLine && webSocketSingleton.isConnected();
    await applyOp(operation, { requireConfirmed });

    // Offline/unknown work stays journaled and keeps its cut clipboard. Never
    // let a delayed confirmation clear newer copy/cut contents.
    if (shouldClearClipboard && requireConfirmed && clipboardRevision !== undefined) {
      const { clipboardStore } = await import('@/shared/stores/clipboardStore');
      clipboardStore.clearIfRevision(clipboardRevision);
    }
  }, [applyOp]);
  
  const duplicateNode = useCallback(async (nodeId: string) => {
    const currentProjectData = projectDataRef.current;
    if (!currentProjectData) return;
    const operation = createDuplicateNodeOperation(currentProjectData, nodeId);
    if (operation) await applyOp(operation);
  }, [applyOp]);
  
  const duplicateSubtree = useCallback(async (nodeId: string) => {
    const currentProjectData = projectDataRef.current;
    if (!currentProjectData) return;
    const operation = await createDuplicateSubtreeOperation(currentProjectData, nodeId);
    if (operation) await applyOp(operation);
  }, [applyOp]);

  // File operations (using extracted utilities)
  const attachFile = useCallback(async (nodeId: string, fileId: string, fileData?: FileAttachment) => {
    const currentProjectData = projectDataRef.current;
    if (!currentProjectData) return;
    const operation = createAttachFileOperation(currentProjectData, nodeId, fileId, fileData);
    if (operation) await applyOp(operation);
  }, [applyOp]);

  const detachFile = useCallback(async (nodeId: string, fileId: string) => {
    const currentProjectData = projectDataRef.current;
    if (!currentProjectData) return;
    const operation = createDetachFileOperation(currentProjectData, nodeId, fileId);
    if (operation) await applyOp(operation);
  }, [applyOp]);

  const deleteFile = useCallback(async (fileId: string) => {
    const currentProjectData = projectDataRef.current;
    if (!currentProjectData) return;
    const operation = createDeleteFileOperation(currentProjectData, fileId);
    if (operation) await applyOp(operation);
  }, [applyOp]);

  const renameFile = useCallback(async (fileId: string, newName: string) => {
    const currentProjectData = projectDataRef.current;
    if (!currentProjectData) return;
    const operation = createRenameFileOperation(currentProjectData, fileId, newName);
    if (operation) await applyOp(operation);
  }, [applyOp]);

  return {
    applyOp,
    copyNodes,
    cutNodes,
    pasteNodes,
    duplicateNode,
    duplicateSubtree,
    attachFile,
    detachFile,
    deleteFile,
    renameFile
  };
}
